import '@/App.css';
import { Fragment } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import useStore from '@/store/useStore';
import GuestLayout from '@/layouts/GuestLayout';
import MerchantLayout from '@/layouts/MerchantLayout';
import LandingPage from '@/pages/guest/LandingPage';
import LoginPage from '@/pages/guest/LoginPage';
import StoreEntryPage from '@/pages/guest/StoreEntryPage';
import GuestPhoneLoginPage from '@/pages/guest/GuestPhoneLoginPage';
import RestaurantSelectionPage from '@/pages/guest/RestaurantSelectionPage';
import WalletPage from '@/pages/guest/WalletPage';
import RewardsPage from '@/pages/guest/RewardsPage';
import HistoryPage from '@/pages/guest/HistoryPage';
import ProfilePage from '@/pages/guest/ProfilePage';
import MerchantLoginPage from '@/pages/merchant/MerchantLoginPage';
import MerchantOnboardingPage from '@/pages/merchant/MerchantOnboardingPage';
import DashboardPage from '@/pages/merchant/DashboardPage';
import CustomersPage from '@/pages/merchant/CustomersPage';
import CustomersOldPage from '@/pages/merchant/CustomersOldPage';
import RewardsOperationsPage from '@/pages/merchant/RewardsOperationsPage';
import RedemptionsPage from '@/pages/merchant/RedemptionsPage';
import RedemptionsTapPage from '@/pages/merchant/RedemptionsTapPage';
import CampaignsPage from '@/pages/merchant/CampaignsPage';
import AiInsightsPage from '@/pages/merchant/AiInsightsPage';
import SettingsPage from '@/pages/merchant/SettingsPage';
import InvoicePage from '@/pages/guest/InvoicePage';
import { DEFAULT_STORE_ID, getSelectedMerchantId, setSelectedMerchantId, storeRoute } from '@/lib/routes';

function GuestProtected({ children }) {
  const { merchantId } = useParams();
  const { guestToken } = useStore();
  const storeId = merchantId || DEFAULT_STORE_ID;

  if (!guestToken) return <Navigate to="/store" replace />;
  return <Fragment key={storeId}>{children}</Fragment>;
}

function MerchantProtected({ children }) {
  const { merchantToken, merchantUser } = useStore();
  if (!merchantToken) return <Navigate to="/merchant" replace />;
  if (merchantUser && !merchantUser.onboarding_complete) return <Navigate to="/merchant/onboarding" replace />;
  return children;
}

function StoreFallbackRedirect() {
  const { guestToken, guestUser, merchantToken, merchantUser } = useStore();

  if (merchantToken) {
    if (merchantUser && !merchantUser.onboarding_complete) {
      return <Navigate to="/merchant/onboarding" replace />;
    }
    return <Navigate to="/merchant/dashboard" replace />;
  }

  if (guestToken) {
    const selectedMerchantId = getSelectedMerchantId();
    if (selectedMerchantId) {
      return <Navigate to={storeRoute(selectedMerchantId, 'wallet')} replace />;
    }
    // Migration safety: hydrate selected_merchant_id from legacy guest session once.
    if (guestUser?.merchant_id) {
      setSelectedMerchantId(guestUser.merchant_id);
      return <Navigate to={storeRoute(guestUser.merchant_id, 'wallet')} replace />;
    }
  }

  return <Navigate to="/store" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Guest Routes */}
        <Route path="/store" element={<StoreEntryPage />} />
        <Route path="/store/login" element={<GuestPhoneLoginPage />} />
        <Route path="/store/select" element={<RestaurantSelectionPage />} />
        <Route path="/store/:merchantId" element={<LandingPage />} />
        <Route path="/store/:merchantId/login" element={<LoginPage />} />
        <Route element={<GuestProtected><GuestLayout /></GuestProtected>}>
          <Route path="/store/:merchantId/wallet" element={<WalletPage />} />
          <Route path="/store/:merchantId/rewards" element={<RewardsPage />} />
          <Route path="/store/:merchantId/history" element={<HistoryPage />} />
          <Route path="/store/:merchantId/profile" element={<ProfilePage />} />
        </Route>
        <Route path="/invoice/:invoiceCode" element={<InvoicePage />} />

        {/* Merchant Routes */}
        <Route path="/merchant" element={<MerchantLoginPage />} />
        <Route path="/merchant/onboarding" element={<MerchantOnboardingPage />} />
        <Route element={<MerchantProtected><MerchantLayout /></MerchantProtected>}>
          <Route path="/merchant/dashboard" element={<DashboardPage />} />
          <Route path="/merchant/campaigns" element={<CampaignsPage />} />
          <Route path="/merchant/customers-old" element={<CustomersOldPage />} />
          <Route path="/merchant/customers" element={<CustomersPage />} />
          <Route path="/merchant/rewards" element={<RewardsOperationsPage />} />
          <Route path="/merchant/redemptions" element={<RedemptionsPage />} />
          <Route path="/merchant/redemptions/tap" element={<RedemptionsTapPage />} />
          <Route path="/merchant/ai" element={<AiInsightsPage />} />
          <Route path="/merchant/settings" element={<SettingsPage />} />
        </Route>

        {/* Fallback */}
        <Route path="/" element={<StoreFallbackRedirect />} />
        <Route path="*" element={<StoreFallbackRedirect />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
